import type { FormField, FormResponseValue } from "@tellescope/types-models";
import { convertFormFieldType } from "./convert-form-field-type";
import { isValueEmpty } from "./is-value-empty";
import { responsesSatisfyConditions } from "./responses-satisfy-conditions";
import type { NextFieldLogicOptions } from "./types";

export interface AreRequiredSubFieldsFilledArgs {
  /** The Question Group whose sub-fields are being checked */
  group: FormField;
  /** All fields by id, used to look up each sub-field's own metadata */
  fieldsMap: Record<string, FormField | undefined>;
  responsesByFieldId: Record<string, FormResponseValue | undefined>;
  /** Every answer the logic evaluator may reference when testing `groupShowCondition` */
  allResponses: FormResponseValue[];
  /** Sub-fields whose repeat/confirmation input disagrees with the main value */
  repeatMismatchFieldIds: ReadonlySet<string>;
  logicOptions?: NextFieldLogicOptions;
}

/**
 * Whether a Question Group can be advanced past — every required, currently visible
 * sub-field has a usable answer.
 *
 * A sub-field passes trivially when it is optional, has no metadata, renders no input
 * (display-only), or is hidden by an unsatisfied `groupShowCondition`. Otherwise it
 * needs a non-empty value; signatures additionally need `signed` to be true, and a
 * sub-field with a repeat mismatch fails even when filled.
 *
 * This is the single group-emptiness rule: the wizard's Next and auto-advance call it directly,
 * and `resolveFieldValidity` and `isFormComplete` delegate their Question Group branches to it
 * rather than re-deriving one. Whether a *filled* sub-field is also **valid** is a separate
 * question answered elsewhere — `useFieldValidation` recurses into sub-fields and lifts the first
 * error onto the group's own id, so callers reject a group by its `fieldErrors` entry.
 */
export const areRequiredSubFieldsFilled = ({
  group,
  fieldsMap,
  responsesByFieldId,
  allResponses,
  repeatMismatchFieldIds,
  logicOptions,
}: AreRequiredSubFieldsFilledArgs): boolean =>
  (group.options?.subFields ?? []).every((sf) => {
    const subFieldMeta = fieldsMap[sf.id];
    if (!subFieldMeta || subFieldMeta.isOptional) return true;
    if (convertFormFieldType(subFieldMeta.type) === null) return true;

    if (subFieldMeta.groupShowCondition && Object.keys(subFieldMeta.groupShowCondition).length > 0) {
      const isVisible = responsesSatisfyConditions(allResponses, subFieldMeta.groupShowCondition, logicOptions);
      if (!isVisible) return true;
    }

    const subResponse = responsesByFieldId[sf.id];
    if (!subResponse || isValueEmpty(subResponse.answer?.value)) return false;

    if (subFieldMeta.type === "signature") {
      const answerValue = subResponse.answer?.value;
      if (typeof answerValue === "object" && answerValue !== null) {
        if ("signed" in answerValue && !answerValue.signed) return false;
        const typedName =
          "fullName" in answerValue && typeof answerValue.fullName === "string" ? answerValue.fullName : "";
        if (!typedName.trim()) return false;
      }
    }

    return !repeatMismatchFieldIds.has(sf.id);
  });
